import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { ScrollArea } from '@/ui/components/scroll-area';
import { cn } from '@/ui/utils';
import { Button } from '@/ui/components/button';
import { Icons } from '@/ui/components/icons';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/ui/components/input/input-group';
import { Icon } from '@/ui/components/icon';
import { Spinner } from '@/ui/components/spinner';
import {
  ArrowLeftIcon,
  Link04Icon,
  NewOfficeIcon,
  Search01Icon,
  Tick02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { useCopyToClipboard } from '@/ui/hooks/use-copy-to-clipboard';
import { Link, useNavigate } from '@tanstack/react-router';
import { LinkProps } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useSuspenseQuery } from '@tanstack/react-query';
import { projectDetailsQuery } from '@/features/projects/queries';
import { getProjectClientDisplay } from '@/features/projects/utils';
import { useParams } from '@tanstack/react-router';
import { Group, GroupItem } from '@/ui/components/group';

export function Main({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <main
      className={cn(
        'flex min-w-0 grow flex-col relative h-[calc(100svh-var(--bottom-navigation-height))] overflow-hidden overflow-x-hidden bg-white md:mt-(--main-margin) md:mx-(--main-margin) md:h-[calc(100svh-calc(var(--main-margin)*2))] md:grow md:rounded-lg md:border @container',
        className,
      )}
    >
      {children}
    </main>
  );
}

export function MainScrollArea({
  viewportClassName,
  ...props
}: React.ComponentProps<typeof ScrollArea>) {
  return (
    <ScrollArea
      viewportClassName={cn(
        'absolute inset-0 min-w-0 scroll-py-4 flex flex-col',
        viewportClassName,
      )}
      {...props}
    />
  );
}

export function MainPending({ className }: { className?: string }) {
  return (
    <Main className={className}>
      <Spinner kind="inset" />
    </Main>
  );
}

export function MainContainer({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('w-full @container px-x pt-4 pb-8', className)} {...props} />;
}

export function MainAlert({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="main-alert"
      className={cn(
        'sticky top-0 z-50 w-full shrink-0 bg-white empty:hidden **:data-[slot=main-alert]:static **:data-[slot=alert]:rounded-none **:data-[slot=alert]:border-x-0 **:data-[slot=alert]:border-t-0',
        className,
      )}
      {...props}
    />
  );
}

export function MainFrames({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('grid @4xl:grid-cols-2 gap-3 @4xl:gap-5 @8xl:grid-cols-3', className)}
      {...props}
    />
  );
}

export function MainHeader({ children, className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex gap-3 flex-wrap items-center justify-between w-full px-x py-3.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function MainHeaderCopyButton(props: React.ComponentProps<typeof Button>) {
  const copy = useCopyToClipboard();

  return (
    <Button
      aria-label={copy.copied ? 'URL copied' : 'Copy URL'}
      onClick={() => copy.trigger(window.location.href)}
      variant="ghost"
      size="sm"
      kind="icon"
      {...props}
    >
      <Icon icon={copy.copied ? Tick02Icon : Link04Icon} />
    </Button>
  );
}

interface MainHeaderBackButtonProps extends LinkProps {
  className?: string;
}

export function MainHeaderBackButton({
  className,
  to = '..',
  ...props
}: MainHeaderBackButtonProps) {
  const navigate = useNavigate();

  return (
    <Button
      aria-label="Go back"
      onClick={() => navigate({ to, ...props })}
      variant="ghost"
      kind="icon"
      className={cn('w-7 my-auto mr-2 -ml-1.75', className)}
    >
      <Icon icon={ArrowLeftIcon} className="size-5" />
    </Button>
  );
}

export function MainHeaderGroup({ className, ...props }: React.ComponentProps<typeof Group>) {
  return <Group className={cn('max-md:w-full', className)} {...props} />;
}
export function MainHeaderGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof GroupItem>) {
  return <GroupItem className={cn('max-md:w-full', className)} {...props} />;
}

export function MainActions({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="main-actions"
      className={cn(
        'has-data-[slot="main-actions-scroll-area"]:gap-1.5 grid-cols-[auto_1fr] shrink-0 min-h-10.5 py-1.5 gap-x-1.5 gap-y-1 border-y md:flex grid items-center justify-between',
        className,
      )}
      {...props}
    />
  );
}

export function MainActionsScrollArea({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="main-actions-scroll-area"
      className={cn(
        'flex items-center min-w-0 gap-1.5 px-1.75 md:px-0 col-span-2 scrollbar-hidden overflow-x-auto order-3',
        className,
      )}
      {...props}
    />
  );
}

export function MainActionsEnd({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="main-actions-end"
      className={cn(
        'flex items-center min-w-0 gap-0.5 ml-auto justify-self-end col-start-2 mr-1.5 md:order-last order-2',
        className,
      )}
      {...props}
    />
  );
}

export function MainActionsButton({ className, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button
      data-slot="main-actions-button"
      variant="ghost"
      size="sm"
      kind="with-icon"
      className={className}
      {...props}
    />
  );
}

export function MainActionsFilterButton({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <MainActionsButton className={cn('justify-self-start ml-1.5 order-1', className)} {...props}>
      <Icons.filters />
      {children ?? t({ en: 'Filters', nb: 'Filter' })}
    </MainActionsButton>
  );
}

export function MainActionsSearchInput({
  className,
  ...props
}: React.ComponentProps<typeof InputGroupInput>) {
  return (
    <InputGroup
      variant="ghost"
      size="sm"
      className={cn(
        'h-7 w-7 min-w-7 overflow-hidden cursor-pointer border border-transparent transition-[width,padding,background-color,border-color] duration-[200ms,200ms,var(--default-transition-duration),var(--default-transition-duration)] ease-out hover:bg-surface-3 [:focus-within,:has(input:not(:placeholder-shown))]:w-56 [:focus-within,:has(input:not(:placeholder-shown))]:cursor-text [:focus-within,:has(input:not(:placeholder-shown))]:bg-white [:focus-within,:has(input:not(:placeholder-shown))]:border-surface-6! [:focus-within,:has(input:not(:placeholder-shown))]:shadow-xs [:focus-within,:has(input:not(:placeholder-shown))]:ps-0.5!',
        className,
      )}
    >
      <InputGroupAddon className="self-stretch ps-[calc(--spacing(2)-1px)]! cursor-[inherit]">
        <Icon icon={Search01Icon} className="opacity-100" />
      </InputGroupAddon>
      <InputGroupInput
        placeholder={t(common.search)}
        aria-label={t(common.search)}
        className="h-full min-w-0 pb-px cursor-[inherit]"
        {...props}
      />
    </InputGroup>
  );
}

export function MainNav({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav data-slot="main-nav" className={cn('flex min-w-0 relative z-0', className)} {...props} />
  );
}

export function MainNavList({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="main-nav-list"
      className={cn(
        'flex px-x has-data-[variant="ghost"]:pl-[calc(var(--padding-x)-var(--spacing)*1.75)] gap-1 min-w-0 overflow-x-auto scrollbar-hidden py-4 md:pt-2 -my-2',
        className,
      )}
      {...props}
    />
  );
}

export function MainNavItem({ className, ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="main-nav-item" className={cn('[&_svg]:size-4', className)} {...props} />;
}

export function MainNavCompanyItem() {
  const { projectId } = useParams({ from: '/_authed/project/$projectId' });
  const auth = useAuth();
  const { data: project } = useSuspenseQuery(projectDetailsQuery(projectId));

  const clientDisplay = getProjectClientDisplay(project, auth.user.organizationName ?? '');
  if (!clientDisplay) return null;

  return (
    <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
      <Icon icon={NewOfficeIcon} />
      {clientDisplay.name} {clientDisplay.isSelf ? t(common.you) : null}
    </MainNavItem>
  );
}
interface MainNavLinkProps extends LinkProps {
  active?: boolean;
  className?: string;
}

export function MainNavLink({ className, children, to, ...props }: MainNavLinkProps) {
  return (
    <Button
      nativeButton={false}
      variant="ghost"
      size="sm"
      render={
        <Link
          to={to}
          data-slot="main-nav-link"
          className={cn(
            'flex text-muted hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-3 aria-[current=page]:text-foreground h-7 pl-1.75 pr-2 rounded-md items-center gap-1.5',
            className,
          )}
          {...props}
        >
          {children}
        </Link>
      }
    />
  );
}

export function MainNavButton({ className, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button
      data-slot="main-nav-button"
      variant="ghost"
      size="sm"
      className={cn(
        'flex text-muted hover:text-foreground h-7 pl-1.75 pr-2 items-center gap-1.5',
        className,
      )}
      {...props}
    />
  );
}
